import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';

const BrokerRegistrationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    companyName: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

 const selectedPlan = searchParams.get('plan') || 'starter';
  const skipTrial = searchParams.get('trial') === 'false';

  const getPlanLimit = (plan: string) => {
    if (plan === 'starter' || plan === 'small' || plan === 'small-business') return 20;
if (plan === 'professional' || plan === 'medium' || plan === 'medium-business') return 50;
    return 999;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const generateCompanyCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      // Calculate trial dates & generate code
      const now = new Date();
      const trialEnd = new Date(now);
      trialEnd.setDate(trialEnd.getDate() + 7);
      const companyCode = generateCompanyCode();
      const carrierLimit = getPlanLimit(selectedPlan);

      // Create auth user with profile metadata
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            role: 'broker',
            plan: 'Pro',
            company_name: formData.companyName,
            first_name: formData.firstName,
            last_name: formData.lastName,
            company_code: companyCode,
            carrier_limit: carrierLimit,
            subscription_status: skipTrial ? 'active' : 'trial',
            trial_started_at: skipTrial ? null : now.toISOString(),
            trial_ends_at: skipTrial ? null : trialEnd.toISOString(),
          }
        }
      });

      if (signUpError) {
        setError(signUpError.message);
        setLoading(false);
        return;
      }

      if (!authData.user) {
        setError('Registration failed. Please try again.');
        setLoading(false);
        return;
      }

      // Success - redirect to login with message
      const successMessage = skipTrial
        ? `Registration successful! Your broker code is: ${companyCode}. Please check your email to verify your account, then proceed to payment.`
        : `Registration successful! Your broker code is: ${companyCode}. You have a 7-day free trial. Please check your email to verify your account.`;

      navigate('/login', {
        state: { message: successMessage }
      });

    } catch (err: any) {
      console.error('Registration error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Register Your Brokerage
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Start onboarding carriers with LoadDrive
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">
                Brokerage Name *
              </label>
              <input
                type="text"
                name="companyName"
                id="companyName"
                required
                value={formData.companyName}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                Your First Name *
              </label>
              <input
                type="text"
                name="firstName"
                id="firstName"
                required
                value={formData.firstName}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                Your Last Name *
              </label>
              <input
                type="text"
                name="lastName"
                id="lastName"
                required
                value={formData.lastName}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                id="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password *
              </label>
              <input
                type="password"
                name="password"
                id="password"
                required
                value={formData.password}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password *
              </label>
              <input
                type="password"
                name="confirmPassword"
                id="confirmPassword"
                required
                value={formData.confirmPassword}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              {skipTrial ? (
                <>
                  <p className="text-sm text-blue-800 font-semibold mb-1">
                    Selected Plan: {selectedPlan === 'starter' ? 'Starter ($29/mo)' :
selectedPlan === 'professional' ? 'Professional ($79/mo)' :
'Enterprise ($199/mo)'}
                  </p>
                  <p className="text-sm text-blue-800">
                    After registration, you'll be directed to complete payment to activate your account.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm text-blue-800 font-semibold mb-1">
                    7-Day Free Trial - {selectedPlan === 'starter' ? 'Starter Plan' :
                                       selectedPlan === 'professional' ? 'Professional Plan' :
                                       'Enterprise Plan'}
                  </p>
                  <p className="text-sm text-blue-800">
                    Start onboarding carriers immediately. No credit card required. Full access to all features.
                  </p>
                </>
              )}
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading ? 'Creating Account...' : 'Create Broker Account'}
              </button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <a href="/login" className="text-blue-600 hover:underline font-medium">
                Log in
              </a>
            </p>
            <p className="text-sm text-gray-600 mt-2">
              Are you a carrier?{' '}
              <a href="/register/carrier" className="text-blue-600 hover:underline font-medium">
                Register with broker code
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrokerRegistrationPage;